import { CloudActions, CloudErrors, LicenseUtils, Product } from '@portal/portal-core';
import get from 'lodash-es/get';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import FullPageFormContainer from 'components/full-page-form-container';
import { fjButtonTypes, FjCheckbox, FjInput } from 'components/primitives';
import { Button } from 'components/styled-primitives';
import StatusWarningBorderedIcon from 'icons/status-warning-bordered';
import type { RootState } from 'reducers';
// `utils` is an untyped .js module; urlParameterValues resolves as `any`.
import { urlParameterValues } from 'utils';

const AlertIcon = styled(StatusWarningBorderedIcon).attrs(({ theme }) => ({
  style: {
    primaryColor: theme.colors.alertRed,
  },
  size: '1.2em',
}))`
  display: inline;
  margin-right: 0.4em;
`;

const { productName } = urlParameterValues() as { productName: string };

const productDisplayName = (): React.ReactNode => {
  if (productName === Product.NAMES.flowJo) {
    return (
      <>
        FlowJo<sup>TM</sup> V11
      </>
    );
  }
  return Product.PRODUCT_DISPLAY_NAMES[productName];
};

const FormInput = styled(FjInput).attrs(({ theme }) => ({
  borderColor: theme.colors.selectableLightBg,
  height: 40,
}))`
  width: 360px;
  font-size: ${({ theme }) => theme.fontSizes.p};
`;

const Form = styled.form`
  width: 360px;

  > * {
    margin-bottom: 5px;
  }
`;

const SubmitButton = styled(Button).attrs({
  id: 'submit-button',
  buttonType: fjButtonTypes.LG,
})`
  margin-top: 20px;
  margin-bottom: 10px;
`;

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.entryModalBg};
  margin: 0 auto 60px;
  padding: ${({ theme: { grid } }) => grid.units(2)};
  height: fit-content;
  box-shadow: 0.18em 0.2em 0.58em #c7c7c7;
  color: ${({ theme }) => theme.colors.text};
  align-self: center;
  display: flex;
  flex-direction: column;
`;

const HeaderText = styled.h2`
  color: ${({ theme }) => theme.colors.selectableLightBg};
  margin: 0.4em auto 0.7em auto;
`;

const AlreadyActiveText = styled.p`
  margin-top: 0px;
`;

interface ProductTrialProps {
  userId?: string | null;
  costCenters?: Record<string, unknown>;
  alerts?: Record<string, unknown>;
  // Only `colors.alertRed` is read; kept loose so partial themes (e.g. tests) are accepted.
  theme: { colors: { alertRed?: string } };
  createProductTrial: (...args: Parameters<typeof CloudActions.registerProductTrial>) => void;
}

export const ProductTrial = ({
  userId,
  costCenters,
  alerts,
  theme,
  createProductTrial,
}: ProductTrialProps): React.ReactElement | null => {
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [postalcode, setPostalcode] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptedDataProcessing, setAcceptedDataProcessing] = useState(true);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);

  const renderAlertBox = (message: React.ReactNode): React.ReactElement => (
    <div
      data-test="product-trial-form-alert"
      className="fj-error-container"
      style={{ marginBottom: '.8em', padding: '3%' }}
    >
      <span id="cannot-create-product-trial-error" className="fj-prevalidation">
        {message}
      </span>
    </div>
  );

  const renderAlerts = (): React.ReactElement | null => {
    if (
      alerts &&
      alerts[CloudErrors.cloudErrorTypes.CANNOT_REGISTER_PRODUCT_TRIAL_REGION_RESTRICTED]
    ) {
      return renderAlertBox(
        <>
          This trial is not available in your region. Please{' '}
          <a href="https://www.bdbiosciences.com/infinicyt">contact us</a> for more information.
        </>,
      );
    }

    if (alerts && alerts[CloudErrors.cloudErrorTypes.CANNOT_REGISTER_SEQ_GEQ_TRIAL]) {
      return renderAlertBox(
        <>Could not create a new {productDisplayName()} trial subscription.</>,
      );
    }

    return null;
  };

  const createTrial = (ev: React.FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();

    if (!postalcode) {
      return;
    }

    createProductTrial(
      company,
      undefined, // degreeGranting (depreciated)
      undefined, // lab (depreciated)
      phone,
      undefined, // fieldOfResearch (depreciated)
      undefined, // country (get on server from user object)
      undefined, // referralBlurb (depreciated)
      productName,
      title,
      postalcode,
      acceptedDataProcessing,
      acceptedMarketing,
    );
  };

  // Cloud-core types `userId`/`costCenters` more narrowly than this component's runtime
  // contract (both may be undefined here); assert to satisfy the typed boundary.
  const trialInfo = LicenseUtils.productTrialAndExpirationDate(
    productName,
    userId as string,
    costCenters as Parameters<typeof LicenseUtils.productTrialAndExpirationDate>[2],
  );
  if (costCenters && trialInfo) {
    const expirationDate = new Date(trialInfo.endDate);
    return (
      <FullPageFormContainer>
        <Container>
          <AlreadyActiveText>
            User has previously activated {Product.PRODUCT_DISPLAY_NAMES[productName]} trial
            license. Trial expiration {'   '}
            {expirationDate.toLocaleDateString()}
          </AlreadyActiveText>
          {/* `alt` is not a standard anchor attribute but is preserved from the original markup. */}
          <a href="/" {...{ alt: 'Return to My Account' }}>
            Return to My Account
          </a>
        </Container>
      </FullPageFormContainer>
    );
  }
  if (userId) {
    return (
      <FullPageFormContainer>
        <Container>
          <HeaderText>{productDisplayName()} Software Trial Sign Up</HeaderText>
          <Form data-test="product-trial-form" onSubmit={createTrial}>
            {renderAlerts()}
            <FormInput
              id="company"
              value={company}
              onChange={(val: string) => setCompany(val)}
              placeholder="Company / Institution (required)"
            />
            <FormInput
              id="title"
              value={title}
              onChange={(val: string) => setTitle(val)}
              placeholder="Title/Position"
            />
            <FormInput
              id="phone"
              value={phone}
              onChange={(val: string) => setPhone(val)}
              placeholder="Phone"
            />
            <FormInput
              id="postalcode"
              value={postalcode}
              onChange={(val: string) => setPostalcode(val)}
              placeholder="Postal Code (required)"
            />
            <div>
              <p>
                BD and its subsidiaries are committed to protecting and respecting your privacy,
                and we’ll only use your personal information to administer your account and to
                provide the products and services you requested from us. More information on how
                BD and its subsidiaries protect your personal data can be found by selecting the
                following links and accessing the privacy section at the bottom of your region
                website:
              </p>
              <ul>
                <li>
                  FlowJo™ Software: <a href="https://flowjo.com/">https://flowjo.com/</a>
                </li>
                <li>
                  BD Infinicyt™ Software: <a href="https://www.bd.com/">https://www.bd.com/</a>
                </li>
              </ul>
              <FjCheckbox
                data-testid="accept-data-processing-checkbox"
                onChange={() => setAcceptedDataProcessing((prev) => !prev)}
                checked={acceptedDataProcessing}
                label="I agree to allow BD and its subsidiaries to store and process my personal data (required)."
              />
              {!acceptedDataProcessing ? (
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5em' }}>
                  <AlertIcon />
                  <span style={{ color: theme.colors.alertRed }}>
                    You must accept the data processing agreement to proceed.
                  </span>
                </div>
              ) : null}
              <p>
                From time to time, we would like to contact you about our products and services, as
                well as other content that may be of interest to you.
              </p>
              <FjCheckbox
                data-testid="marketing-opt-in-checkbox"
                onChange={() => setAcceptedMarketing((prev) => !prev)}
                checked={acceptedMarketing}
                label="I agree to receive marketing communications from BD and its subsidiaries (optional)."
              />
              <p>
                You may unsubscribe from marketing communications at any time. For more information
                on how to unsubscribe, our privacy practices, and how we are committed to
                protecting and respecting your privacy, please review our{' '}
                <a href="https://www.flowjo.com/privacy-policy">Privacy Policy</a>.
              </p>
            </div>

            <SubmitButton
              disabled={!postalcode || !acceptedDataProcessing}
              width="100%"
              type="submit"
              tabIndex={0}
            >
              Submit
            </SubmitButton>
          </Form>
        </Container>
      </FullPageFormContainer>
    );
  }
  return null;
};

const ProductTrialContainer = (): React.ReactElement => {
  const userId = useSelector((state: RootState) => get(state.auth, 'userId'));
  const costCenters = useSelector((state: RootState) => state.costCenters);
  const alerts = useSelector((state: RootState) => state.alerts);
  const theme = useTheme();
  const dispatch = useDispatch();

  const createProductTrial = (
    ...args: Parameters<typeof CloudActions.registerProductTrial>
  ): void => {
    dispatch(CloudActions.registerProductTrial(...args));
  };

  return (
    <ProductTrial
      userId={userId}
      costCenters={costCenters}
      alerts={alerts}
      theme={theme}
      createProductTrial={createProductTrial}
    />
  );
};

export default ProductTrialContainer;
